import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  BellIcon,
  CalendarPlusIcon,
  CheckCircle2Icon,
  FileTextIcon,
  PackageIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  UserIcon,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import type { ProjectStatus, TaskPriority, TaskStatus } from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { FilterBar, FilterField } from '@/components/filter-bar'
import { PageHeader } from '@/components/page-header'
import { ProjectRefBadge, ProjectStatusBadge } from '@/components/projects/project-badges'
import { SegmentedControl } from '@/components/segmented-control'
import { DueDate } from '@/components/tasks/due-date'
import {
  ClientRequestBadge,
  SubtaskCounter,
  TaskPriorityIcon,
  TaskStatusBadge,
} from '@/components/tasks/task-badges'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { PROJECT_STATUS_LABELS, TASK_PRIORITY_LABELS, TASK_STATUS_LABELS } from '@/lib/labels'

/**
 * Page spécimen du système visuel (passe de design du 2026-09-18) : l'appbar,
 * le menu et le bandeau de titre dans la même matière acier, le corps en cadres
 * blancs sur gris acier. Chaque écran s'y conforme ; elle se valide une fois.
 * Contenus réels du produit, aucun appel réseau : elle se lit hors ligne.
 */
export const Route = createFileRoute('/_app/design')({
  component: DesignPage,
})

const PROJECT = {
  id: 'p1',
  ref: 'GPBLOC:GP-REWRITE',
  name: 'Rewrite de l’app',
  status: 'active' as const,
}

const TASKS: {
  key: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  due: string | null
  subtasks: [number, number]
  request?: boolean
  milestone?: string
}[] = [
  {
    key: 'GPBLOC-283',
    title: 'Évaluation du coût pour l’app répartiteur',
    status: 'backlog',
    priority: 'high',
    due: null,
    subtasks: [0, 0],
    request: true,
  },
  {
    key: 'GPBLOC-271',
    title: 'Double exécution et bascule',
    status: 'todo',
    priority: 'medium',
    due: '2027-02-05',
    subtasks: [0, 5],
    milestone: 'Phase 8 — Migration et bascule',
  },
  {
    key: 'GPBLOC-212',
    title: 'Moteur de calcul',
    status: 'in_progress',
    priority: 'medium',
    due: '2026-12-11',
    subtasks: [2, 5],
    milestone: 'Phase 5 — Soumissions',
  },
  {
    key: 'GPBLOC-127',
    title: 'Invitations',
    status: 'in_review',
    priority: 'low',
    due: '2026-10-16',
    subtasks: [3, 3],
    milestone: 'Phase 1 — Socle',
  },
  {
    key: 'GPBLOC-121',
    title: 'Authentification sécurisée',
    status: 'done',
    priority: 'urgent',
    due: '2026-09-12',
    subtasks: [5, 5],
  },
]

const TASK_STATUSES: TaskStatus[] = [
  'backlog',
  'todo',
  'in_progress',
  'in_review',
  'done',
  'cancelled',
]
const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent']
const PROJECT_STATUSES: ProjectStatus[] = ['active', 'on_hold', 'completed', 'cancelled']

/** Un spécimen : ce qu'il montre, la règle qu'il fixe, puis l'exemple. */
function Specimen({
  title,
  rule,
  children,
}: {
  title: string
  rule: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-4">
      <div className="max-w-3xl space-y-1">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        <p className="text-sm text-pretty text-muted-foreground">{rule}</p>
      </div>
      {children}
    </section>
  )
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  )
}

function DesignPage() {
  const [vue, setVue] = useState<'liste' | 'kanban' | 'gantt'>('liste')
  const [terminees, setTerminees] = useState(false)
  const [visible, setVisible] = useState(true)

  return (
    <div className="space-y-10">
      <PageHeader
        title="Système visuel"
        documentTitle="Système visuel"
        description="Appbar, menu et bandeau dans la même matière acier ; le corps en cadres blancs sur gris acier. Chaque écran s’y conforme."
        actions={
          <>
            <Button variant="outline">Exporter les jetons</Button>
            <Button>
              <PlusIcon />
              Nouvelle tâche
            </Button>
          </>
        }
      />

      <Specimen
        title="Bandeau de page"
        rule="Un bandeau acier clair en tête de chaque écran, collé au menu et à l’appbar, d’un bord à l’autre — montré ici encastré. Le titre y est seul, ou avec un retour au-dessus, une description et des actions, ou des onglets posés sur son bord bas. Au plus un geste principal reste visible ; les autres vont dans le menu « ⋯ », sur la ligne du titre, à droite. Les boutons y sont monochromes ; la couleur d’action reste au contenu."
      >
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
          <PageHeader embedded title="Relances" documentTitle="Système visuel" />

          <PageHeader
            embedded
            documentTitle="Système visuel"
            title="Bonjour, Marc."
            description="Vos tâches en cours, vos échéances et votre temps de la semaine."
            actions={
              <Button>
                <PlusIcon />
                Nouvelle tâche
              </Button>
            }
          />

          <PageHeader
            embedded
            documentTitle="Système visuel"
            eyebrow={
              <Button asChild variant="ghost" size="sm">
                <Link to="/projects">
                  <ArrowLeftIcon />
                  Projets
                </Link>
              </Button>
            }
            title={PROJECT.name}
            description={
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-key text-sm">{PROJECT.ref}</span>
                <span aria-hidden="true">·</span>
                <a href="#gp-bloc">GP Bloc</a>
                <ProjectStatusBadge status="active" />
              </span>
            }
            actions={
              <>
                <Button variant="outline">
                  <FileTextIcon />
                  Cahier des charges
                  <span className="ml-1 text-xs tabular-nums opacity-70">0/3</span>
                </Button>
                <Button variant="outline">
                  <PackageIcon />
                  Livrables
                  <span className="ml-1 text-xs tabular-nums opacity-70">0/1</span>
                </Button>
              </>
            }
            menu={
              <>
                <DropdownMenuItem>
                  <PencilIcon />
                  Modifier
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive">
                  <Trash2Icon />
                  Supprimer
                </DropdownMenuItem>
              </>
            }
          />

          <Tabs defaultValue="apercu">
            <PageHeader
              embedded
              documentTitle="Système visuel"
              eyebrow={
                <Button asChild variant="ghost" size="sm">
                  <Link to="/clients" search={{ sort: 'name', page: 1, status: 'active' }}>
                    <ArrowLeftIcon />
                    Clients
                  </Link>
                </Button>
              }
              title="GP Bloc"
              description="Fabricant de blocs de béton, Saint-Hyacinthe · client depuis 2024"
              actions={
                <Button variant="outline">
                  <PencilIcon />
                  Modifier
                </Button>
              }
              tabs={
                <TabsList>
                  <TabsTrigger value="apercu">Vue d’ensemble</TabsTrigger>
                  <TabsTrigger value="projets">
                    Projets <span className="text-xs tabular-nums opacity-70">3</span>
                  </TabsTrigger>
                  <TabsTrigger value="contacts">
                    Contacts <span className="text-xs tabular-nums opacity-70">4</span>
                  </TabsTrigger>
                  <TabsTrigger value="documents">
                    Documents <span className="text-xs tabular-nums opacity-70">42</span>
                  </TabsTrigger>
                </TabsList>
              }
            />
          </Tabs>
        </div>
      </Specimen>

      <Specimen
        title="Fiche à sections"
        rule="Une fiche plafonne à 100 rem et se lit en deux colonnes : le corps à gauche, les détails dans un cadre étroit à droite. Chaque section a son cadre blanc ; les titres de cadre sont le seul niveau de hiérarchie sous le bandeau."
      >
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_20rem]">
          <div className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Budget</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-2xl font-semibold tabular-nums">
                  32 h <span className="text-base font-normal text-muted-foreground">sur 80 h</span>
                </p>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full w-2/5 rounded-full bg-primary" />
                </div>
                <p className="text-xs text-muted-foreground">40 % du budget · 28 h facturables</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent className="markdown max-w-prose">
                <p>
                  Réécriture de l’application de soumissions de GP Bloc : catalogue, assistant de
                  soumission en dix étapes, moteur de calcul, export vers Sage 50. Bascule prévue en
                  huit phases, la production actuelle restant servie jusqu’à la double exécution.
                </p>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Détails</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3">
                <DetailRow label="Début">21 sept. 2026</DetailRow>
                <DetailRow label="Échéance">30 juin 2027</DetailRow>
                <DetailRow label="Responsable">Marc Scalzolaro</DetailRow>
                <DetailRow label="Tâches">191 ouvertes, 12 terminées</DetailRow>
                <DetailRow label="Facturable">Oui, par défaut</DetailRow>
              </dl>
            </CardContent>
          </Card>
        </div>
      </Specimen>

      <Specimen
        title="Liste avec filtres"
        rule="Une liste occupe toute la largeur. En ligne : le basculement de vue, la recherche, le bouton « Filtres » qui compte les filtres posés, et le geste principal à droite. Les filtres se choisissent dans un panneau latéral (plein écran sur téléphone) ; les filtres posés se lisent en chips, chacun avec sa croix. Les étiquettes d’une ligne passent à la ligne au lieu d’élargir le tableau."
      >
        <Card>
          <CardContent className="space-y-4 pt-6">
            <FilterBar
              view={
                <SegmentedControl
                  label="Affichage des tâches"
                  value={vue}
                  onChange={setVue}
                  options={[
                    { value: 'liste', label: 'Liste' },
                    { value: 'kanban', label: 'Kanban' },
                    { value: 'gantt', label: 'Gantt' },
                  ]}
                />
              }
              search={
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Titre, clé, description…" className="w-64 pl-8" />
                </div>
              }
              actions={
                <Button>
                  <PlusIcon />
                  Nouvelle tâche
                </Button>
              }
              active={[
                { key: 'client', label: 'Client GP Bloc', onClear: () => undefined },
                ...(terminees
                  ? [
                      {
                        key: 'terminees',
                        label: 'Terminées comprises',
                        onClear: () => {
                          setTerminees(false)
                        },
                      },
                    ]
                  : []),
              ]}
              onClearAll={() => {
                setTerminees(false)
              }}
              panelTitle="Filtrer les tâches"
            >
              <FilterField label="Client">
                <Select defaultValue="gpbloc">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous les clients</SelectItem>
                    <SelectItem value="gpbloc">GP Bloc</SelectItem>
                    <SelectItem value="acme">ACME inc.</SelectItem>
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Assignation">
                <Button variant="outline" className="justify-start">
                  <UserIcon />
                  Mes tâches
                </Button>
              </FilterField>
              <div className="flex items-center gap-2">
                <Switch id="spec-terminees" checked={terminees} onCheckedChange={setTerminees} />
                <Label htmlFor="spec-terminees">Inclure les terminées</Label>
              </div>
            </FilterBar>
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableHead className="w-28">Clé</TableHead>
                    <TableHead>Titre</TableHead>
                    <TableHead className="w-40">Statut</TableHead>
                    <TableHead className="w-10">Prio.</TableHead>
                    <TableHead className="w-32">Échéance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {TASKS.filter((task) => terminees || task.status !== 'done').map((task) => (
                    <TableRow key={task.key}>
                      <TableCell className="font-key whitespace-nowrap text-muted-foreground">
                        {task.key}
                      </TableCell>
                      <TableCell>
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-medium">{task.title}</span>
                          <SubtaskCounter done={task.subtasks[0]} total={task.subtasks[1]} />
                          <ProjectRefBadge project={PROJECT} />
                          <ClientRequestBadge clientRequest={task.request ?? false} />
                          {task.milestone && <Badge variant="outline">{task.milestone}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <TaskStatusBadge status={task.status} />
                      </TableCell>
                      <TableCell>
                        <TaskPriorityIcon priority={task.priority} />
                      </TableCell>
                      <TableCell>
                        <DueDate dueDate={task.due} status={task.status} className="text-sm" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </Specimen>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        <Specimen
          title="Formulaire"
          rule="Un formulaire est un cadre : champs empilés, libellés au-dessus, les actions dans le pied du cadre, la principale à droite."
        >
          <Card>
            <CardHeader>
              <CardTitle>Nouvelle tâche</CardTitle>
              <CardDescription>Dans {PROJECT.name}, pour GP Bloc.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="spec-titre">Titre</Label>
                <Input id="spec-titre" defaultValue="Export facture Sage 50 .imp" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="spec-priorite">Priorité</Label>
                  <Select defaultValue="medium">
                    <SelectTrigger id="spec-priorite">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {TASK_PRIORITY_LABELS[priority]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spec-echeance">Échéance</Label>
                  <Input id="spec-echeance" type="date" defaultValue="2027-01-08" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spec-description">Description</Label>
                <Textarea
                  id="spec-description"
                  rows={3}
                  defaultValue="Générer le fichier .imp attendu par Sage 50 à partir d’une facture validée, avec les comptes de la phase 6."
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="spec-visible" className="flex-col items-start gap-0.5">
                  Visible par le client
                  <span className="text-xs font-normal text-muted-foreground">
                    Le contact de GP Bloc la verra dans son espace.
                  </span>
                </Label>
                <Switch id="spec-visible" checked={visible} onCheckedChange={setVisible} />
              </div>
            </CardContent>
            <CardFooter className="justify-end">
              <Button variant="ghost">Annuler</Button>
              <Button>Créer la tâche</Button>
            </CardFooter>
          </Card>
        </Specimen>

        <Specimen
          title="Onglets dans un cadre"
          rule="Sous le bandeau, les onglets d’une section vivent dans son cadre, soulignés de la couleur d’action."
        >
          <Card>
            <CardContent className="pt-6">
              <Tabs defaultValue="temps">
                <TabsList>
                  <TabsTrigger value="temps">Temps</TabsTrigger>
                  <TabsTrigger value="activite">Activité</TabsTrigger>
                  <TabsTrigger value="commentaires">
                    Commentaires <span className="text-xs tabular-nums opacity-70">2</span>
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="temps" className="space-y-2 text-sm">
                  <p>
                    <span className="font-semibold tabular-nums">8 h 57</span> cette semaine, dont
                    <span className="tabular-nums"> 6 h 30</span> facturables.
                  </p>
                  <p className="text-muted-foreground">
                    Dernière saisie : hier, 1 h 15 sur GPBLOC-212 « Moteur de calcul ».
                  </p>
                </TabsContent>
                <TabsContent value="activite" className="text-sm text-muted-foreground">
                  Camille Roy a approuvé la section « Paiement en ligne » il y a 2 h.
                </TabsContent>
                <TabsContent value="commentaires" className="text-sm text-muted-foreground">
                  Deux commentaires, le dernier de Luc Martin ce matin.
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </Specimen>
      </div>

      <Specimen
        title="Badges et statuts"
        rule="Les statuts et priorités gardent leurs couleurs d’état ; le texte des badges emploie la teinte forte pour rester lisible. Sur acier, tout badge passe blanc translucide."
      >
        <Card>
          <CardContent className="space-y-5 pt-6">
            <div className="flex flex-wrap items-center gap-2">
              {TASK_STATUSES.map((status) => (
                <TaskStatusBadge key={status} status={status} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {PRIORITIES.map((priority) => (
                <TaskPriorityIcon key={priority} priority={priority} withLabel />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {PROJECT_STATUSES.map((status) => (
                <ProjectStatusBadge key={status} status={status} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ProjectRefBadge project={PROJECT} />
              <ClientRequestBadge clientRequest />
              <Badge variant="info">Client</Badge>
              <Badge variant="outline">Phase 5 — Soumissions</Badge>
              <Badge variant="muted">hors-perimetre</Badge>
              <Badge variant="warning">Écrit au journal, non envoyé</Badge>
              <Badge variant="success">Approuvée r2</Badge>
              <Badge variant="destructive">En retard</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Libellés : {TASK_STATUSES.map((status) => TASK_STATUS_LABELS[status]).join(', ')} ·{' '}
              {PROJECT_STATUSES.map((status) => PROJECT_STATUS_LABELS[status]).join(', ')}.
            </p>
          </CardContent>
        </Card>
      </Specimen>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        <Specimen
          title="État vide"
          rule="Un cadre vide est une invitation à agir : ce qu’il n’y a pas, pourquoi, et le geste qui le remplit."
        >
          <Card>
            <CardContent className="pt-6">
              <EmptyState
                icon={BellIcon}
                title="Aucune relance en attente"
                description="Tous vos échanges prévus sont passés ou notés. La prochaine relance apparaîtra ici la veille."
                action={
                  <Button variant="outline">
                    <CalendarPlusIcon />
                    Planifier un échange
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </Specimen>

        <Specimen
          title="Dialogue et notification"
          rule="Un dialogue confirme un geste qu’on ne défait pas ; il nomme l’objet. Une notification dit ce qui vient d’arriver, avec le même mot que le bouton."
        >
          <Card>
            <CardContent className="flex flex-wrap items-center gap-2 pt-6">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">Clore la tâche</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Clore GPBLOC-271 ?</DialogTitle>
                    <DialogDescription>
                      « Double exécution et bascule » passera à Terminée. Ses 5 sous-tâches ouvertes
                      resteront ouvertes, et le chronomètre en cours sera arrêté.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="ghost">Annuler</Button>
                    <Button
                      onClick={() => {
                        toast.success('GPBLOC-271 close.')
                      }}
                    >
                      <CheckCircle2Icon />
                      Clore la tâche
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button
                variant="outline"
                onClick={() => {
                  toast.success(
                    'Section « Paiement en ligne » envoyée. Camille Roy sera prévenue par courriel.',
                  )
                }}
              >
                Notification de succès
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  toast.error(
                    'Envoi impossible : la section a été modifiée depuis votre lecture. Rechargez-la.',
                  )
                }}
              >
                Notification d’erreur
              </Button>
            </CardContent>
          </Card>
        </Specimen>
      </div>
    </div>
  )
}
